import React, { useState } from 'react';
import Input from '#lib/components/input.jsx';
import Button from '#lib/components/button.jsx';
import FilePreview from '#components/file-preview.jsx';
import ImagePreview from '#components/images/image-preview.jsx';
import { useOpenClose } from '#lib/hooks/use-boolean-state.js';
import { ChangePasswordDialog } from '#routes/settings/password.jsx';
import Switch from '#lib/components/switch.jsx';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import { Icon } from '@iconify-icon/react';
import { buttonLooks, sizes } from '#lib/styles/constants.js';
import Dialog from '#lib/components/dialog.jsx';
import { LoadingIndicator, LoadingSkeleton } from '#lib/components/loading.jsx';
import { isValid } from '#lib/helpers/errors.js';
import toast from 'react-hot-toast';
import FloatingTooltip from '#components/tooltip.jsx';
import { generaPassword } from '#helpers/helpers.js';

// THEME COMPONENT
export function Detail(props) {
    const {
        selectedUser,
        handleChange,
        handleChangeCheckbox,
        handleResetPassword,
        errors,
        imageProfileData,
        goToAccount,
        isLoading
    } = props;
    // gli errori vengono tracciati con l'oggetto errors che è un dizionario di oggetti con:
    // chiave: campo del profile
    // valore: oggetto con
    //          - text: stringa motivo dell'errore
    //          - value: true significa che il rispettivo campo è in errore
    const [isOpen, setIsOpen] = useState(false);
    const changePasswordModal = useOpenClose();

    const imageProfile = imageProfileData?.data?.meta;

    if (isLoading)
        return (
            <LoadingSkeleton className="w-full h-1/3 flex items-center justify-center md:px-4 md:border-l md:border-l-neutral-200">
                <LoadingIndicator />
            </LoadingSkeleton>
        );

    if (!selectedUser)
        return (
            <section className="hidden w-full md:flex p-16 flex-col items-center gap-6 md:px-4 md:border-l md:border-l-neutral-200 lg:bg-neutral-50">
                <p className="text-2xl font-bold text-neutral-500">{_t.users.emptyDetailText()}</p>
                <p className="text-lg text-center font-bold text-neutral-400">{_t.users.emptyDetailSubText()}</p>
            </section>
        );

    if (selectedUser.main) {
        return (
            <section className="w-full flex p-12 flex-col items-center gap-6 md:px-4 md:border-l md:border-l-neutral-200 lg:bg-neutral-50 pt-2">
                <p className="w-full md:w-3/4 text-lg text-center font-bold text-neutral-400">
                    {_t.users.mainUserSubTitle()}
                </p>
                <GoToButton className="w-2/3 md:max-w-[250px] lg:w-1/2" onClick={goToAccount}>
                    {_t.users.goToAccount()}
                </GoToButton>
            </section>
        );
    }
    return (
        <div className="w-full md:h-full md:px-4 flex flex-col space-y-3 md:border-l md:border-l-neutral-200">
            <div className="w-full max-w-[750px] flex flex-col gap-5">
                <div className="flex gap-5 w-full">
                    <p className="w-full text-lg font-bold text-neutral-700">{_t.users.userDetailTitle()}</p>
                </div>
                <div className="flex flex-col space-y-2 w-full">
                    <p className="mb-2">{_t.users.imageProfile()}</p>
                    <div className="w-[160px] h-[160px]">
                        <FilePreview
                            key={selectedUser.id}
                            name="imageProfile"
                            file={imageProfile}
                            coords={imageProfile && imageProfile?.coords}
                            width={270}
                            height={270}
                            isSquare={true}
                            rounded={true}
                            accept="image/png, image/jpeg"
                            isGallery={false}
                            {...imageProfileData?.handlers}
                        >
                            {({ previewFile }) => {
                                const initialState = {
                                    ...previewFile?.coords,
                                    containerWidth: 270
                                };
                                const src = previewFile?.tempSrc ?? previewFile?.src;
                                return (
                                    <>{previewFile && <ImagePreview imageSrc={src} initialState={initialState} />}</>
                                );
                            }}
                        </FilePreview>
                    </div>
                </div>
                <div className="flex justify-between gap-5 h-[36px] w-full lg:w-1/2">
                    {!selectedUser.isNew && (
                        <Button
                            className={addClasses('w-1/2 h-full flex justify-center items-center')}
                            size={'xs'}
                            onClick={() => setIsOpen(true)}
                            wrapText={true}
                        >
                            {_t.users.resetPassword()}
                        </Button>
                    )}
                    <div className="flex justify-start w-1/2">
                        <Switch.Full
                            name="enabled"
                            checked={selectedUser.enabled}
                            label={_t.users.enabledCheckbox()}
                            onChange={handleChangeCheckbox}
                        />
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-5 w-full">
                    <div className="flex-col space-y-2 w-full md:w-1/2">
                        <p>{_t.users.firstName()}</p>
                        <Input.Full
                            type="text"
                            value={selectedUser.firstName || ''}
                            name="firstName"
                            placeholder={_t.users.firstName()}
                            onChange={handleChange}
                            hasError={errors?.firstName?.value}
                            autoComplete="off"
                        />
                    </div>
                    <div className="flex-col space-y-2 w-full md:w-1/2">
                        <p>{_t.users.lastName()}</p>
                        <Input.Full
                            type="text"
                            value={selectedUser.lastName || ''}
                            name="lastName"
                            placeholder={_t.users.lastName()}
                            onChange={handleChange}
                            hasError={errors?.lastName?.value}
                            autoComplete="off"
                        />
                    </div>
                </div>
                <div className="flex flex-col items-start lg:flex-row gap-5 w-full pb-4">
                    <div className="flex flex-col md:flex-row gap-5 w-full lg:w-1/2">
                        <div className="flex-col space-y-2 w-full">
                            <p>{_t.users.phoneNumber()}</p>
                            <Input.Full
                                type="text"
                                value={selectedUser.phoneNumber || ''}
                                name="phoneNumber"
                                placeholder={_t.users.phoneNumber()}
                                onChange={handleChange}
                                hasError={errors?.phoneNumber?.value}
                                autoComplete="off"
                            />
                            <div
                                className={cn(
                                    'text-sm',
                                    errors?.phoneNumber?.value ? 'text-red-500' : 'text-neutral-600'
                                )}
                            >
                                {_t.users.phoneNumberDescription()}
                            </div>
                        </div>
                    </div>
                    <div className="flex-col space-y-2 w-full lg:w-1/2">
                        <p>{_t.users.email()}</p>
                        <Input.Full
                            type="text"
                            value={selectedUser.email || ''}
                            name="email"
                            placeholder={_t.users.email()}
                            onChange={handleChange}
                            hasError={errors?.email?.value}
                            autoComplete="off"
                        />
                    </div>
                </div>

                <ResetPasswordModal isOpen={isOpen} onClose={() => setIsOpen(false)} onClick={handleResetPassword} />
                <ChangePasswordDialog isOpen={changePasswordModal.isOpen} onClose={changePasswordModal.close} />
            </div>
        </div>
    );
}

export function ResetPasswordModal(props) {
    const { isOpen, onClose, onClick } = props;
    const [password, setPassword] = useState(null);
    const [readOnly, setReadOnly] = useState(false);
    const [hasError, setHasError] = useState(false);
    const _onClick = () => {
        if (!isValid({ password }, 'password', 'password')) return setHasError(true);
        onClick(password);
        setReadOnly(true);
    };

    const _onClose = () => {
        setReadOnly(false);
        setPassword(null);
        setHasError(false);
        onClose();
    };

    return (
        <Dialog isOpen={isOpen} className="h-screen flex items-center justify-center fixed inset-4 ">
            <Dialog.Panel className="w-full max-w-[400px] mx-10 flex flex-col gap-3 z-50">
                <div className="pr-4 text-lg">
                    <b> {_t.users.resetPasswordTitle()} </b>
                    <Dialog.CloseButton onClick={_onClose} />
                </div>
                <div>
                    <p className="text-sm"> {_t.users.resetPasswordSubTitle()} </p>
                </div>
                <div className="flex gap-2">
                    <div className="relative w-[160px] flex gap-2 items-center flex-grow">
                        <input
                            name="password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (hasError && isValid({ password: e.target.value }, 'password', 'password'))
                                    setHasError(false);
                            }}
                            placeholder={_t.auth.password()}
                            readOnly={readOnly}
                            className={cn(
                                'input-box input-inside-elements p-2 w-full',
                                hasError && 'text-red-500 focus-within:ring-red-500 border-red-500'
                            )}
                        />
                        {!readOnly && (
                            <FloatingTooltip
                                placement="top"
                                content={_t.auth.generatePassword()}
                                refClassName="absolute top-[6px] right-2 cursor-pointer hover:text-primary-600"
                            >
                                <Icon
                                    icon="arcticons:masterpassword"
                                    width={'1.6rem'}
                                    height={'1.6rem'}
                                    onClick={() => setPassword(() => generaPassword())}
                                />
                            </FloatingTooltip>
                        )}
                    </div>
                    {readOnly && (
                        <Button look="primary">
                            <Icon
                                icon="material-symbols:content-copy-outline-rounded"
                                width="1.3rem"
                                height="1.3rem"
                                onClick={() => {
                                    navigator.clipboard.writeText(password);
                                    toast.success(_t.crud.itinerary.copySuccessfull());
                                }}
                            />
                        </Button>
                    )}
                </div>
                <p className={cn('text-xs text-neutral-600', hasError && 'text-red-500')}>
                    {_t.auth.passwordLegenda()}
                </p>
                <div className="w-full flex justify-end">
                    <Button className={addClasses('w-1/3')} as="button" onClick={_onClick} wrapText={true}>
                        {_t.global.confirm()}
                    </Button>
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}

export function GoToButton(props) {
    const { children, className, ...rest } = props;
    return (
        <Button
            className={addClasses(
                mergeClasses(
                    'relative w-full flex justify-center items-center gap-x-2 px-4 py-1 mb-0 text-neutral-600 rounded border-2 cursor-pointer',
                    className
                )
            )}
            look={buttonLooks.primary}
            size={sizes.sm}
            type="button"
            wrapText={true}
            {...rest}
        >
            <div className="h-full flex items-center">{children || _t.global.goTo()}</div>
            <div className="h-full flex items-center">
                <Icon icon="fluent:arrow-right-12-filled" width="1.2rem" height="1.2rem" />
            </div>
        </Button>
    );
}
